import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useOcelMetadata } from "../../../stores/OcelContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import ObjectTableSettings from "./ObjectTableSettings";

const ROWS_PER_PAGE = 15;
const FETCH_LIMIT = 1000;

// ── Sort chevron ────────────────────────────────────────────────────────────
// Defined at module level so React always sees the same component type and
// never unmounts/remounts the SVG on re-renders (which caused the layout jump).
const SortIcon = ({ col, sortBy, sortOrder }) => {
  if (sortBy !== col) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return sortOrder === "asc" ? (
    <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
};

const ObjectTable = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  // Only dateRange and activityFilters count toward the badge — component config
  // fields (objectType, selectedColumns, componentTitle) are not "filters".
  const hasObjectTableLocalFilters = !!(
    localFilters.dateRange?.enabled || localFilters.activityFilters?.enabled
  );
  const objectTableLocalFilterCount =
    (localFilters.dateRange?.enabled ? 1 : 0) +
    (localFilters.activityFilters?.enabled ? 1 : 0);

  // Component configuration. The fields the first fetch depends on start from
  // the saved config synchronously: the mount-time auto-load runs before the
  // restore effect below has re-rendered, and would otherwise fire one request
  // with the defaults (all object types, no columns) whose response could land
  // after — and overwrite — the correct one.
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState(() => initialSettings?.objectType ?? "");
  const [selectedColumns, setSelectedColumns] = useState(() => initialSettings?.selectedColumns ?? []);

  // Data state
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  // Table UX
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Derived metadata — availableObjectTypes filtered by global object-type filter only
  // (local objectTypes filter must not restrict settings dropdown options)
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    const allTypes = Object.keys(metadata.object_types).sort();
    const filtered =
      globalFilterState?.objectTypes?.length > 0
        ? allTypes.filter((t) => globalFilterState.objectTypes.includes(t))
        : allTypes;
    setAvailableObjectTypes(filtered);
    // Auto-reset if selected type is no longer available under global filters
    if (objectType && !filtered.includes(objectType)) {
      setObjectType(filtered[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterState?.objectTypes, metadata]);

  const availableAttributes = useMemo(
    () => metadata?.object_attribute_names || [],
    [metadata]
  );

  const availableActivities = useMemo(
    () =>
      metadata?.activity_types
        ? Object.keys(metadata.activity_types).sort()
        : [],
    [metadata]
  );

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.selectedColumns !== undefined) setSelectedColumns(initialSettings.selectedColumns);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ componentTitle, objectType, selectedColumns, localFilters }));
  }, [componentTitle, objectType, selectedColumns, localFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Settings modal lifecycle ──────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback(
    (val) => {
      setIsSettingsModalOpen(val);
      if (!val) cancelDraftFilters();
    },
    [cancelDraftFilters]
  );

  // ── Data fetching ─────────────────────────────────────────────────────────
  // Only the most recently started fetch may commit its result — a slower,
  // older request must not overwrite a newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const response = await api.post("/ocel/object-table", {
        object_type: objectType || undefined,
        selected_attributes:
          selectedColumns.length > 0 ? selectedColumns : undefined,
        limit: FETCH_LIMIT,
        offset: 0,
      });
      if (seq !== fetchSeqRef.current) return;
      setTableData(response);
      setCurrentPage(1);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Failed to load object table");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, objectType, selectedColumns]);

  // Fire after Apply commits new config + filters
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  // Auto-reload when filters or active file changes (after first load)
  useEffect(() => {
    if (hasFetched) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  // Auto-load from persistent cache (same as AttributeTable)
  useAutoLoad(fetchData, [activeFile, currentFilters], tableData);

  // Register refresh callback
  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
  }, [onRefresh, fetchData]);

  // ── Header actions ────────────────────────────────────────────────────────
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {hasObjectTableLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={hasObjectTableLocalFilters}
            localFilterCount={objectTableLocalFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, hasObjectTableLocalFilters, objectTableLocalFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // Trigger a parent re-render so App.jsx picks up headerActions from its ref.
  // (componentHeaderActions in App.jsx is a useRef — it needs a state change
  //  to cause a re-render that will read the newly set ref value.)
  useEffect(() => {
    if (onTitleChange) onTitleChange(componentTitle || "Object Table");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasObjectTableLocalFilters, componentTitle]); // Re-run when badge appears/disappears, or when a restored title is applied

  // ── Sorting ───────────────────────────────────────────────────────────────
  const sortedRows = useMemo(() => {
    if (!tableData?.rows?.length) return [];
    if (!sortBy) return tableData.rows;

    const rows = [...tableData.rows];
    rows.sort((a, b) => {
      const aVal = a.attributes[sortBy] ?? null;
      const bVal = b.attributes[sortBy] ?? null;

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      const bothNumeric = !isNaN(aNum) && !isNaN(bNum);

      if (bothNumeric) {
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [tableData, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
  const pageRows = sortedRows.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  // ── Pagination helpers ────────────────────────────────────────────────────
  const getPageNumbers = () => {
    const pages = [];
    const window = 2;
    const start = Math.max(1, currentPage - window);
    const end = Math.min(totalPages, currentPage + window);
    if (start > 1) { pages.push(1); if (start > 2) pages.push("..."); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push("..."); pages.push(totalPages); }
    return pages;
  };

  // ── Column list ──────────────────────────────────────────────────────────
  // Use selectedColumns (component state) as the authoritative order so that
  // stale persistent cache responses with a different attribute order don't
  // override what the user configured. Fall back to the response list only
  // when no columns are explicitly selected (default load).
  const columns =
    selectedColumns.length > 0
      ? selectedColumns
      : tableData?.selected_attributes || [];
  const displayTitle = componentTitle || "Object Table";

  return (
    <BaseTile
      title={displayTitle}
      loading={loading}
      error={error}
      minHeight="default"
    >
      {/* Placeholder when not yet configured */}
      {!hasFetched && !loading && !error && (
        <TileStatusContent>
          <div className="text-center py-8">
            <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18M10 3v18M14 3v18" />
            </svg>
            <p className="text-sm text-gray-500">Open Settings to configure columns and load data</p>
          </div>
        </TileStatusContent>
      )}

      {/* Table */}
      {hasFetched && !loading && !error && tableData && (
        <TileContent padding="none">
          <div className="flex flex-col h-full">
            {/* Scrollable table area */}
            <div className="flex-1 overflow-auto" style={{ scrollbarGutter: "stable" }}>
              <table className="w-full text-sm table-fixed">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {/* Counter column */}
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap border-b border-gray-200 w-12">
                      #
                    </th>
                    {/* Attribute columns */}
                    {columns.map((col) => (
                      <th
                        key={col}
                        className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider overflow-hidden cursor-pointer hover:bg-gray-100 select-none border-b border-gray-200"
                        title={col}
                        onClick={() => handleSort(col)}
                      >
                        <span className="flex items-center gap-1">
                          <span className="truncate">{col}</span>
                          <SortIcon col={col} sortBy={sortBy} sortOrder={sortOrder} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 bg-white">
                  {pageRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 py-8 text-center text-sm text-gray-400"
                      >
                        No objects found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row, idx) => (
                      <tr key={row.object_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2 text-xs text-gray-400 whitespace-nowrap w-12">
                          {(currentPage - 1) * ROWS_PER_PAGE + idx + 1}
                        </td>
                        {columns.map((col) => {
                          const val = row.attributes[col];
                          const display =
                            val === null || val === undefined
                              ? "—"
                              : String(val);
                          return (
                            <td
                              key={col}
                              className="px-4 py-2 text-xs text-gray-700 max-w-0 truncate"
                              title={display !== "—" ? display : undefined}
                            >
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Aggregation footer */}
                {tableData.aggregations && (
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td className="px-4 py-2.5 text-xs font-semibold text-gray-700 whitespace-nowrap">
                        Total:{" "}
                        {tableData.aggregations.total_objects.toLocaleString()} objects
                      </td>
                      {columns.map((col) => {
                        const sum = tableData.aggregations.numeric_sums?.[col];
                        return (
                          <td
                            key={col}
                            className="px-4 py-2.5 text-xs text-gray-600 text-left max-w-0 truncate"
                          >
                            {sum !== null && sum !== undefined
                              ? `Σ ${Number(sum).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                              : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
                <span className="text-xs text-gray-500">
                  {(currentPage - 1) * ROWS_PER_PAGE + 1}–
                  {Math.min(currentPage * ROWS_PER_PAGE, sortedRows.length)} of{" "}
                  {sortedRows.length.toLocaleString()}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {getPageNumbers().map((page, idx) =>
                    page === "..." ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[28px] h-7 text-xs rounded transition-colors ${
                          currentPage === page
                            ? "bg-blue-600 text-white"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </TileContent>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Object Table Settings"
        getInitialValues={() => ({
          componentTitle,
          objectType,
          selectedColumns,
        })}
        onApply={(values) => {
          setComponentTitle(values.componentTitle);
          setObjectType(values.objectType);
          setSelectedColumns(values.selectedColumns);
          if (onTitleChange && values.componentTitle) {
            onTitleChange(values.componentTitle);
          }
          applyDraftFilters();
          setPendingApply(true);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={false}
      >
        {({ values, setField }) => (
          <ObjectTableSettings
            componentTitle={values.componentTitle}
            onTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => {
              setField("objectType", v);
            }}
            selectedColumns={values.selectedColumns}
            onSelectedColumnsChange={(v) => setField("selectedColumns", v)}
            availableObjectTypes={availableObjectTypes}
            availableAttributes={availableAttributes}
            availableActivities={availableActivities}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            activeFile={activeFile}
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default ObjectTable;
